import { AIHUB_ADMIN_PRIVILEGE } from "../services/permissionsService";
import { useAppStore } from "../stores/store";

export type PrivilegeCheckOptions = {
  /**
   * When false, `aihub.admin` does not satisfy this check.
   * Tab visibility uses this so each tab follows only its own privilege.
   */
  includeAdmin?: boolean;
};

function evaluateHasPrivilege(
  requiredPrivileges: string | string[],
  mode: 'all' | 'any',
  userPrivileges: string[],
  options?: PrivilegeCheckOptions,
): boolean {
  const required = Array.isArray(requiredPrivileges) ? requiredPrivileges : [requiredPrivileges];
  if (required.length === 0) return true;

  // Admin unlocks gated UI actions except checks that opt out (AI Hub tabs).
  if (options?.includeAdmin !== false && userPrivileges.includes(AIHUB_ADMIN_PRIVILEGE)) {
    return true;
  }

  if (mode === 'any') {
    return required.some((p) => userPrivileges.includes(p));
  }
  return required.every((p) => userPrivileges.includes(p));
}

/**
 * Hook to check if current user has a specific privilege or set of privileges
 * @param requiredPrivileges - Single privilege string or array of privileges
 * @param mode - 'all' (must have all) or 'any' (must have at least one)
 *
 * Note: Does not distinguish "still loading" from "loaded but missing". For
 * UIs that need to show a loading state on cold refresh, prefer
 * {@link useHasPrivilegeStatus}.
 */
export function useHasPrivilege(
  requiredPrivileges: string | string[],
  mode: 'all' | 'any' = 'any',
  options?: PrivilegeCheckOptions,
): boolean {
  const userPrivileges = useAppStore((state) => state.ui?.privileges) || [];
  return evaluateHasPrivilege(requiredPrivileges, mode, userPrivileges, options);
}

export type HasPrivilegeStatus = {
  /** True when the user actually has the required privilege(s). False both before privileges load and when truly missing. */
  has: boolean;
  /** True once `/authz/privileges` has resolved at least once for the current session. */
  loaded: boolean;
};

/**
 * Same check as {@link useHasPrivilege} but also returns whether privileges
 * have been loaded yet. Use this when the UI should show a loading state
 * on cold page refresh instead of falsely showing a "no permission" state
 * before `/authz/privileges` returns.
 */
export function useHasPrivilegeStatus(
  requiredPrivileges: string | string[],
  mode: 'all' | 'any' = 'any',
  options?: PrivilegeCheckOptions,
): HasPrivilegeStatus {
  const userPrivileges = useAppStore((state) => state.ui?.privileges) || [];
  const loaded = useAppStore((state) => state.ui?.privilegesLoaded) ?? false;
  const has = evaluateHasPrivilege(requiredPrivileges, mode, userPrivileges, options);
  return { has, loaded };
}
